import {
  ContactTypeMap,
  GovernmentTaxTypeMap,
  OfficeTypeMap,
  TaxOrganizationTypeMap,
} from "../CMS/schema"
import { Provider, ProviderStatusDisplayId } from "../Provider/schema"
import { FileStatusDisplayIdEnum } from "../Files/schema"
import {
  IrsStatusValues,
  TinValidationStatusValues,
} from "../TinValidation/schema"
import { UserAccount } from "../PracticeProviders/useUsersDataForUserIds"
import {
  LiabilityStatusEnum,
  LicenseStatusEnum,
} from "../../features/shared/utils"
import { PracticeRoleEnum, UserAccountStatusEnum } from "../Account/userSlice"
import { TaxInfo } from "../Practices/schema"

export type PracticePhoneTypeDisplayId =
  | "phone-mobile"
  | "phone-all"
  | "phone-office"

// TODO: replace all use of this with the PracticeStatusEnum
export type PracticeStatus =
  | "pending"
  | "pending-activation"
  | "active"
  | "suspended"
  | "deactivated"

export enum PracticeStatusEnum {
  PENDING = "pending",
  PENDING_ACTIVATION = "pending-activation",
  ACTIVE = "active",
  SUSPENDED = "suspended",
  DEACTIVATED = "deactivated",
}

export enum PracticeAffiliateStatusEnum {
  INVITED = "invited",
  PENDING = "pending",
  PENDING_ACTIVATION = "pending-activation",
  ACCEPTED = "accepted",
  ACTIVE = "active",
  DEACTIVATED = "deactivated",
  SUSPENDED = "suspended",
  REMOVED = "removed",
}

// TODO: move this to the CMS - see provider status map in provider schema.ts for reference
export const practiceStatusMap: Record<PracticeStatus, string> = {
  pending: "Pending",
  active: "Active",
  suspended: "Suspended",
  deactivated: "Deactivated",
  "pending-activation": "Pending Activation",
}

export enum PracticeStatusActions {
  activate = "activate",
  deactivate = "deactivate",
  submit = "submit",
  suspend = "suspend",
}

export enum LiabilityInsuranceStatusEnum {
  ACTIVE = "active",
  EXPIRED = "expired",
  EXPIRING_SOON = "expiring-soon",
  PENDING = "pending",
  NOT_AVAILABLE = "not-available",
}

export interface PhoneEmailFax {
  id: string
  value: string
  type: keyof ContactTypeMap | null
  isPrimary: boolean
  error: boolean
}

export interface PhoneEmailFaxWithErrors extends PhoneEmailFax {
  dropdownInputError: boolean
  dropdownInputErrorText: string
  error: boolean
  errorText?: string
}

export interface PracticeAddress {
  addressOne: string
  addressTwo: string
  city: string
  county: string
  stateOrTerritory: string
  zipCode: string
}

export interface PracticeContact {
  phones: PhoneEmailFax[]
  primaryEmail: string
  altEmails: PhoneEmailFax[]
  faxes: PhoneEmailFax[]
}

export interface PracticeDetails {
  logoFile: File | undefined
}

export interface TaxOrgType {
  id: number
  displayId: keyof TaxOrganizationTypeMap
}

export interface GovTaxType {
  id: number
  displayId: keyof GovernmentTaxTypeMap
}

export interface TaxInfoTables {
  taxOrganizationTypes: TaxOrgType[]
  governmentTaxTypes: GovTaxType[]
}

export type TinInfo = {
  irsStatus?: IrsStatusValues
  tinValidationStatus: TinValidationStatusValues
  tinValidatedDateTime?: string
  tinValidationError?: string | unknown
  tinValidatedBy?: string
}

export interface BaseTaxInformation {
  id: string
  governmentTaxIdType?: keyof GovernmentTaxTypeMap
  governmentTaxId: string
  taxOrganizationType?: keyof TaxOrganizationTypeMap
  legalName: string
  altName: string
  file: FileType | undefined
  fileUploadDate: string | null
  fileUploadedBy: string | null
  taxVerified: boolean
}

export type TaxInformation = BaseTaxInformation & TinInfo

export interface AuthAndReleases {
  signedFileId: string | null
  signedOn: Date | null
  signedBy: string | null
  signingUrl: string
}

export interface LiabilityInsurance {
  id: string
  fileId: string
  fileName: string
  issueDate: Date
  expirationDate: Date
  fileStatus: FileStatusDisplayIdEnum
  createdBy: string
  createdDateTime: Date
  lastUpdatedDateTime: Date
  insuranceStatus: string
}

export interface LocationPhoto {
  id: string
  locationPhotoTypeDisplayId: string
  name: string
  description: string
  fileName: string
  fileId: string
  thumbnailFieldId: string
  fileStatus: string
  lastUpdated: string
  createdBy?: string
  createdByFullName?: string
  createdDateTime?: string
  base64Thumbnail?: string
}

export interface LocationEmail {
  id: string | undefined
  address: string
  locationEmailTypeDisplayId: string
}

export interface LocationPhone {
  id: string | undefined
  locationPhoneTypeDisplayId: string
  number: string
  countryCode?: number
}
export interface Location {
  id: string | null
  line1: string | null
  line2: string | null
  city: string | null
  stateOrTerritory: string | null
  postalCode: string | null
  county: string | null
  fax: string | null
  isPrimary: boolean
  isAdaAccessible: boolean
  officeRequirementsCertified: boolean
  createdBy?: string | null
  locationTypeDisplayId: keyof OfficeTypeMap | null
  lastUpdated: string | null
  phones: LocationPhone[]
  emails: LocationEmail[]
  photos?: LocationPhoto[]
}

interface SignedFile {
  signedFileId: string | null
  signedOn: Date | null
  signedBy: string | null
  fileStatusDisplayId: FileStatusDisplayIdEnum | null
  resetOn: Date | null
}

export interface PracticeAgreement {
  practiceAgreement: SignedFile
  knoxKeeneAgreement: SignedFile
  redirectUrl: string
}

export enum LocationTypeDisplayId {
  BUSINESS = "business",
  VIRTUAL = "virtual",
  HOME = "home",
}

export interface VirtualLocation {
  id?: string
  phoneNumber: string
  phoneType: string
  emailAddress: string
  faxNumber?: string
  createdBy?: string | null
  // TODO: combine practice.virtualLocation and practice.locations (physical)
  // Both should be the Location type defined above and source from the /locations endpoint
  locationTypeDisplayId: LocationTypeDisplayId.VIRTUAL
  officeRequirementsCertified: boolean
}

export enum PracticeInviteStatusDisplayId {
  ACCEPTED = "accepted",
  DECLINED = "declined",
  SENT = "sent",
}

export enum PracticeServiceInviteStatusDisplayId {
  ACCEPTED = "accepted",
  DECLINED = "declined",
  EXPIRED = "expired",
  INVITED = "invited",
  PENDING = "pending",
  NOT_APPLICABLE = "not-applicable",
}

export const selectPracticeInviteStatusDisplayId = (
  param: PracticeServiceInviteStatusDisplayId,
): PracticeInviteStatusDisplayId => {
  switch (param) {
    case PracticeServiceInviteStatusDisplayId.ACCEPTED:
      return PracticeInviteStatusDisplayId.ACCEPTED
    case PracticeServiceInviteStatusDisplayId.DECLINED:
      return PracticeInviteStatusDisplayId.DECLINED
    case PracticeServiceInviteStatusDisplayId.INVITED:
    case PracticeServiceInviteStatusDisplayId.PENDING:
      return PracticeInviteStatusDisplayId.SENT
    default:
      // This default is for affiliates without invites
      return PracticeInviteStatusDisplayId.ACCEPTED
  }
}

export const selectPracticeServiceInviteStatusDisplayIds = (
  param: PracticeInviteStatusDisplayId,
): PracticeServiceInviteStatusDisplayId[] => {
  switch (param) {
    case PracticeInviteStatusDisplayId.ACCEPTED:
      return [PracticeServiceInviteStatusDisplayId.ACCEPTED]
    case PracticeInviteStatusDisplayId.DECLINED:
      return [PracticeServiceInviteStatusDisplayId.DECLINED]
    default:
      return [
        PracticeServiceInviteStatusDisplayId.INVITED,
        PracticeServiceInviteStatusDisplayId.PENDING,
        PracticeServiceInviteStatusDisplayId.NOT_APPLICABLE,
      ]
  }
}

// Mirrors the v2 PracticeInviteDtoV2 wire shape (practiceId and providerId now live on the
// parent PracticeAffiliate). Callers that need practiceId/providerId should source them from
// the owning affiliate (or use PracticeInviteWithContext, which stitches them on).
export interface PracticeInvite {
  id: string
  email: string
  practiceName: string
  practiceHasActiveInsurance: boolean
  invitedDate: string
  inviteCode?: string
  userId?: string
  practiceAffiliateId?: string
  userLinkedOn?: string
  userAcceptedOn?: string
  practiceAffiliateStatusDisplayId?: PracticeAffiliateStatusEnum
  inviteStatusDisplayId: PracticeServiceInviteStatusDisplayId
  inviteRoleDisplayIds: PracticeRoleEnum[]
  firstName?: string
  lastName?: string
  createdBy?: string
  // Frontend-only enrichments (not on the v2 wire; loaders/UI populate them when relevant)
  practiceProvidesLiabilityInsurance?: boolean
  practiceStatus?: PracticeStatus
  practiceHasConditionalReqs?: boolean
  isInvitationForProviderRole?: boolean
  practiceHasActiveInvite?: boolean
}

// Invite enriched with parent-affiliate context (practiceId, providerId). Loaders that flatten
// nested affiliate invites should produce this shape so downstream UI can keep reading
// invite.practiceId / invite.providerId without re-traversing to the parent affiliate.
export interface PracticeInviteWithContext extends PracticeInvite {
  practiceId: string
  providerId?: string
}

export interface PracticeInviteDetails {
  id: string
  practiceName: string
  practiceBillingAddress: {
    city: string
    county: string
    createdBy: string
    createdDateTime: string
    lastUpdatedBy: string
    lastUpdatedDateTime: string
    legacyLocationId: number | null
    line1: string
    line2: string
    postalCode: string
    practiceId: string
    stateOrTerritory: string
  }
  practicePrimaryPhoneNumber: string
  practicePrimaryEmail: string
  inviteRoleDisplayIds: PracticeRoleEnum[]
}

export interface PracticeRate {
  rateValue: number
  createdBy: string
  createdDate: string
  isActive: boolean
}

export type PracticeBillingAddress = {
  practiceId: string
  line1: string
  line2: string
  city: string
  stateOrTerritory: string
  postalCode: string
  county: string
  legacyLocationId: number
  createdBy: string
  createdDateTime: string
  lastUpdatedDateTime: string
}

export type PracticeContactInfo = {
  phones: PhoneEmailFax[]
  emails: PhoneEmailFax[]
  faxes: PhoneEmailFax[]
}

export interface Practice {
  id: string
  name: string
  status: PracticeStatus
  isVisible: boolean
  websiteUrl?: string
  overview?: string
  logoImageUrl?: string | null
  deleteDateTime?: string
  createdDateTime: string
  createdBy: string
  lastUpdatedDateTime: string
  authReleaseRequired: boolean | null
  hasOnlineServicesInCalifornia: boolean | null
  hasNoPhysicalLocations: boolean | null
  isKnoxKeeneStale: boolean | null
  isPracticeAgreementStale: boolean | null
  tags: string[]
  taxInfo: TaxInfo
  taxInformation: TaxInformation
  practiceInfo: {
    practiceAddress?: PracticeAddress
    practiceContact?: PracticeContact
    practiceDetails?: PracticeDetails
  }
  providersInfo: Provider[]
  authAndReleases: AuthAndReleases
  practiceAffiliates: PracticeAffiliate[] | null
  billingAddress: PracticeBillingAddress | null
  liabilityInsurances: LiabilityInsurance[]
  locations: Location[] // physical
  locationPhotos: LocationPhoto[]
  virtualLocation: VirtualLocation | null
  practiceAgreement: PracticeAgreement
  practiceInvites: PracticeInvite[]
  legacyAffiliateId: number | null
  practiceTypeDisplayId: string
}

export interface PracticeCompleteness {
  isPracticeInformationComplete: boolean
  isPracticeTaxInformationComplete: boolean
  isPracticeAuthReleaseComplete: boolean
  isPracticeUsingLiabilityInsurance: boolean
  isPracticeLiabilityInformationComplete: boolean
  isPracticeLocationComplete: boolean
  isPracticeAgreementComplete: boolean
}

// Mirrors the v2 PracticeAffiliateDtoV2 wire shape. providerId is set for *every* provider
// affiliate (registered or not) — it is NOT a sentinel for "unregistered". Determine
// unregistered status via the rule:
//   !userId && !!providerId && (practice.practiceTypeDisplayId === 'standard' ||
//                               provider.providerTypeDisplayId === 'standard')
// ('managed' on either side disqualifies.)
export interface PracticeAffiliate {
  id: string
  practiceId: string
  practiceName: string
  roleDisplayIds: PracticeRoleEnum[]
  userId?: string
  providerId?: string
  statusDisplayId?: PracticeAffiliateStatusEnum
  practiceInvites?: PracticeInvite[]
  // Optional frontend-only enrichments populated by loaders (not on v2 wire).
  email?: string
  firstName?: string
  lastName?: string
}

export interface PracticeActivationData {
  id: string
  userId: string
  name: string
  billingAddress: PracticeBillingAddress
  activationRequested: string
}

export interface PracticeLocation extends Location {
  hasNewReferral: boolean
  hasActiveReferral: boolean
  hasSessionScheduled: boolean
}

export interface UserAccountExtended extends UserAccount {
  affiliateId?: string
  fullName: string
  practiceRoleIds: PracticeRoleEnum[]
  statusId: UserAccountStatusEnum
}

export interface UserAccountExtendedV2 extends UserAccount {
  affiliateId: string
  fullName: string
  practiceRoleIds: PracticeRoleEnum[]
  statusId: PracticeAffiliateStatusEnum
}

export interface ProviderExtended extends Provider {
  statusId: ProviderStatusDisplayId
  hasNewReferral: boolean
  hasActiveReferral: boolean
  hasSessionScheduled: boolean
  locations: PracticeLocation[]
  licenseStatusId: LicenseStatusEnum
  insuranceStatusId: LiabilityStatusEnum
}

export interface PracticeInviteExtended extends PracticeInvite {
  statusId: PracticeServiceInviteStatusDisplayId
}

// TODO: Check these to make sure all the types are correct - it also seems weird to have the roles on BOTH the user and invite..
// could probably remove them from one or the other. My suggestion is to stitch them onto the user (affiliate) since unregistered
// providers and practice-creators will not have an invite
export interface PracticeIndividual {
  id: string
  user: UserAccountExtendedV2
  invite?: PracticeInviteExtended
  provider?: ProviderExtended
}

export interface AdpSamlResponse {
  saml2Xml: string
  targetUrl: string
  relayState: string
  isValid: boolean
}

export interface PracticeSetManagedResponse {
  providerIds: string[]
  userIdsToReset: string[]
}

export interface PracticeUpdateManagedAffiliatesRequest {
  activeProviderIds: string[]
  pendingActivationProviderIds: string[]
}

export interface StatusUpdateResult {
  updatedProviderIds: string[]
  failedProviderIds: Map<string, string>
}

export interface ResetInviteResponse {
  userIdsToReset: string[]
  providerIdsToClear: string[]
}

export interface ProviderRemovalResponse {
  providerIdToArchive: string
}

export interface ProviderAddMoveResponse {
  userId?: string
  providerIdLinked?: string
}
